/**
 * Admin Analytics — V2.M.3
 *
 * Operator dashboard that aggregates events captured by
 * /api/analytics/track. Reads via /api/admin/analytics with
 * ADMIN_KEY bearer auth.
 *
 * Sections :
 *   - Overview cards : sessions 24h/7d/30d, auth vs anon, unique IPs
 *   - Top pages, events, tokens, chains
 *   - Geo distribution + browser families
 *   - IP clusters (multi-wallet users on same IP)
 *   - Recent sessions table with details
 *
 * Style aligné V2.L.1 (Stripe-inspired light) : surfaces blanches,
 * bordures fines, accent bleu, gris sobre.
 */

import { useEffect, useState } from 'react';
import { fetchAnalytics } from '../lib/fetchAdminAction';
import '../layout/AdminLayout.css';

type Period = 1 | 7 | 30;

interface OverviewData {
  sessions_24h?: number;
  sessions_7d?: number;
  sessions_30d?: number;
  auth_users_24h?: number;
  auth_users_7d?: number;
  auth_users_30d?: number;
  anon_sessions_24h?: number;
  anon_sessions_7d?: number;
  anon_sessions_30d?: number;
  unique_ips_24h?: number;
}

interface CountRow {
  count: number;
}

interface PageRow extends CountRow {
  path: string;
}
interface EventRow extends CountRow {
  event_type: string;
}
interface TokenRow extends CountRow {
  token_id: string;
  symbol?: string;
}
interface ChainRow extends CountRow {
  chain: string;
}
interface CountryRow {
  country: string;
  sessions: number;
}
interface BrowserRow {
  family: string;
  sessions: number;
}
interface IpClusterRow {
  ip: string;
  wallet_count: number;
  session_count: number;
  user_count: number;
  wallets: string[];
}
interface SessionRow {
  session_id: string;
  user_id: string | null;
  first_seen: string;
  last_seen: string;
  event_count: number;
  ip_address: string | null;
  country: string | null;
  region: string | null;
  city: string | null;
  user_agent: string | null;
  is_authenticated: boolean;
  linked_wallets: Array<{ chain: string; address: string }> | null;
}

// V2.N.8 : délègue au helper partagé fetchAnalytics (JWT admin OU
// legacy key). Conserve la signature (action, days, extra) pour ne pas
// toucher les call sites.
function fetchAction<T>(action: string, days: Period, extra: Record<string, string | number> = {}): Promise<T | null> {
  return fetchAnalytics<T>(action, { days, ...extra });
}

export function Analytics() {
  const [period, setPeriod] = useState<Period>(7);
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [pages, setPages] = useState<PageRow[]>([]);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [tokens, setTokens] = useState<TokenRow[]>([]);
  const [chains, setChains] = useState<ChainRow[]>([]);
  const [countries, setCountries] = useState<CountryRow[]>([]);
  const [browsers, setBrowsers] = useState<BrowserRow[]>([]);
  const [clusters, setClusters] = useState<IpClusterRow[]>([]);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [o, p, e, t, c, co, b, cl, s] = await Promise.all([
          fetchAction<OverviewData>('overview', period),
          fetchAction<PageRow[]>('top_pages', period, { limit: 20 }),
          fetchAction<EventRow[]>('top_events', period),
          fetchAction<TokenRow[]>('top_tokens', period),
          fetchAction<ChainRow[]>('top_chains', period),
          fetchAction<CountryRow[]>('countries', period, { limit: 15 }),
          fetchAction<BrowserRow[]>('browsers', period),
          fetchAction<IpClusterRow[]>('ip_clusters', period, { min_wallets: 2 }),
          fetchAction<SessionRow[]>('recent_sessions', period, { limit: 50 }),
        ]);
        if (cancelled) return;
        setOverview(o);
        setPages(p || []);
        setEvents(e || []);
        setTokens(t || []);
        setChains(c || []);
        setCountries(co || []);
        setBrowsers(b || []);
        setClusters(cl || []);
        setSessions(s || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [period]);

  const auth24 = overview?.auth_users_24h ?? 0;
  const sessions24 = overview?.sessions_24h ?? 0;
  const anon24 = overview?.anon_sessions_24h ?? 0;

  return (
    <main style={pageStyle}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Analytics</h1>
          <div style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '4px' }}>
            Données capturées par /api/analytics/track. Fenêtre : <strong>{period === 1 ? '24 heures' : `${period} jours`}</strong>.
          </div>
        </div>
        <PeriodSelector value={period} onChange={setPeriod} />
      </header>

      {/* Overview */}
      <Grid>
        <StatCard
          label="Sessions (24h)"
          value={sessions24}
          sub={`${auth24} auth · ${anon24} anon`}
          accent="#0969da"
        />
        <StatCard
          label={`Sessions (${period === 1 ? '24h' : `${period}j`})`}
          value={period === 1 ? sessions24 : period === 7 ? overview?.sessions_7d ?? 0 : overview?.sessions_30d ?? 0}
          sub={`Utilisateurs uniques sur la période`}
          accent="#0e9f6e"
        />
        <StatCard
          label="Auth users"
          value={period === 1 ? auth24 : period === 7 ? overview?.auth_users_7d ?? 0 : overview?.auth_users_30d ?? 0}
          sub="Comptes connectés"
          accent="#6366f1"
        />
        <StatCard
          label="IPs uniques (24h)"
          value={overview?.unique_ips_24h ?? 0}
          sub="Distinct IP fingerprints"
          accent="#d97706"
        />
      </Grid>

      {loading && <div style={{ marginTop: '20px', fontSize: '13px', color: 'var(--muted)' }}>Chargement…</div>}

      <Section title="Top pages">
        <CountTable
          headers={['Path', 'Vues']}
          rows={pages.map((r) => [r.path, String(r.count)])}
          emptyHint="Aucune page_view enregistrée pour la période."
        />
      </Section>

      <TwoCol>
        <Section title="Top events">
          <CountTable
            headers={['Event type', 'Count']}
            rows={events.map((r) => [r.event_type, String(r.count)])}
            emptyHint="Pas d'événements."
          />
        </Section>
        <Section title="Top chains liées">
          <CountTable
            headers={['Chain', 'Liens']}
            rows={chains.map((r) => [r.chain, String(r.count)])}
            emptyHint="Aucun wallet linké sur la période."
          />
        </Section>
      </TwoCol>

      <TwoCol>
        <Section title="Top tokens ajoutés">
          <CountTable
            headers={['Token', 'Adds']}
            rows={tokens.map((r) => [`${r.symbol || r.token_id} (${r.token_id})`, String(r.count)])}
            emptyHint="Aucun token ajouté sur la période."
          />
        </Section>
        <Section title="Géo (top pays)">
          <CountTable
            headers={['Pays', 'Sessions']}
            rows={countries.map((r) => [r.country, String(r.sessions)])}
            emptyHint="Pas de données géo."
          />
        </Section>
      </TwoCol>

      <Section title="Navigateurs (family)">
        <CountTable
          headers={['Famille', 'Sessions']}
          rows={browsers.map((r) => [r.family, String(r.sessions)])}
          emptyHint="Pas de données UA."
        />
      </Section>

      <Section
        title="IP clusters (multi-wallets)"
        hint={`IPs qui ont linké ≥ 2 wallets distincts dans les ${period === 1 ? '24h' : `${period}j`}. Permet d'identifier les utilisateurs gérant plusieurs portefeuilles depuis la même connexion.`}
      >
        {clusters.length === 0 ? (
          <Empty hint="Aucun cluster détecté." />
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>IP</th>
                <th style={thStyle}># Wallets</th>
                <th style={thStyle}># Sessions</th>
                <th style={thStyle}># Users</th>
                <th style={thStyle}>Wallets</th>
              </tr>
            </thead>
            <tbody>
              {clusters.map((c) => (
                <tr key={c.ip}>
                  <td style={tdStyle}><code style={codeStyle}>{c.ip}</code></td>
                  <td style={tdStyle}>{c.wallet_count}</td>
                  <td style={tdStyle}>{c.session_count}</td>
                  <td style={tdStyle}>{c.user_count}</td>
                  <td style={{ ...tdStyle, fontSize: '11px' }}>{c.wallets.join(' · ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="Sessions récentes" hint="50 dernières sessions, triées par activité.">
        {sessions.length === 0 ? (
          <Empty hint="Aucune session pour le moment." />
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Last seen</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>IP</th>
                <th style={thStyle}>Geo</th>
                <th style={thStyle}>Browser</th>
                <th style={thStyle}>Events</th>
                <th style={thStyle}>Wallets</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.session_id}>
                  <td style={tdStyle}>{new Date(s.last_seen).toLocaleString('fr-FR')}</td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        fontSize: '10px',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        background: s.is_authenticated ? 'rgba(14, 159, 110, 0.15)' : 'rgba(105, 115, 134, 0.15)',
                        color: s.is_authenticated ? '#0e9f6e' : 'var(--muted)',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                      }}
                    >
                      {s.is_authenticated ? 'AUTH' : 'ANON'}
                    </span>
                  </td>
                  <td style={tdStyle}><code style={codeStyle}>{s.ip_address || '—'}</code></td>
                  <td style={tdStyle}>{[s.city, s.region, s.country].filter(Boolean).join(', ') || '—'}</td>
                  <td style={tdStyle}>{shortenUA(s.user_agent)}</td>
                  <td style={tdStyle}>{s.event_count}</td>
                  <td style={tdStyle}>{(s.linked_wallets || []).map((w) => `${w.chain}:${w.address.slice(0, 8)}…`).join(' · ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </main>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Subcomponents
// ─────────────────────────────────────────────────────────────────────

function PeriodSelector({ value, onChange }: { value: Period; onChange: (v: Period) => void }) {
  return (
    <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
      {([1, 7, 30] as const).map((p) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          style={{
            padding: '8px 14px',
            background: value === p ? 'var(--accent)' : 'transparent',
            color: value === p ? '#ffffff' : 'var(--text)',
            border: 'none',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {p === 1 ? '24h' : `${p}j`}
        </button>
      ))}
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '20px' }}>
      {children}
    </div>
  );
}

function StatCard({ label, value, sub, accent }: { label: string; value: number | string; sub: string; accent: string }) {
  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px' }}>
      <div style={{ fontSize: '11px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: '26px', fontWeight: 700, marginTop: '4px', color: accent, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>{sub}</div>
    </div>
  );
}

function Section({ title, children, hint }: { title: string; children: React.ReactNode; hint?: string }) {
  return (
    <section style={{ marginTop: '24px', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '16px' }}>
      <h2 style={{ fontSize: '15px', fontWeight: 700, margin: 0, marginBottom: hint ? '4px' : '12px', color: 'var(--text)' }}>{title}</h2>
      {hint && <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '12px' }}>{hint}</div>}
      {children}
    </section>
  );
}

function TwoCol({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '16px' }}>{children}</div>;
}

function CountTable({ headers, rows, emptyHint }: { headers: string[]; rows: string[][]; emptyHint: string }) {
  if (rows.length === 0) return <Empty hint={emptyHint} />;
  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          {headers.map((h) => <th key={h} style={thStyle}>{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((cell, j) => (
              <td key={j} style={{ ...tdStyle, fontVariantNumeric: j === r.length - 1 ? 'tabular-nums' : undefined }}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Empty({ hint }: { hint: string }) {
  return <div style={{ fontSize: '12px', color: 'var(--muted)', fontStyle: 'italic' }}>{hint}</div>;
}

function shortenUA(ua: string | null): string {
  if (!ua) return '—';
  if (ua.length > 50) return ua.slice(0, 50) + '…';
  return ua;
}

// ─────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────

const pageStyle: React.CSSProperties = {
  maxWidth: '1400px',
  margin: '0 auto',
  padding: '24px',
  color: 'var(--text)',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '12px',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  borderBottom: '1px solid var(--border)',
  color: 'var(--muted)',
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  fontWeight: 600,
  background: 'transparent',
};

const tdStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: '1px solid var(--border)',
  color: 'var(--text)',
};

const codeStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: '11px',
  color: 'var(--muted)',
};
